import { useCallback, useEffect, useState } from 'react';
import { useNfcScanner } from '../../hooks/useNfcScanner';
import api from '../../services/api';
import type { NfcTagLookupResponse } from '../../types/inspectionOrder';
import './NfcScanButton.css';

interface NfcScanButtonProps {
  orderId: string;
  onDeviceFound: (result: NfcTagLookupResponse) => void;
  onNotInOrder: (result: NfcTagLookupResponse) => void;
  onNotFound: () => void;
  disabled?: boolean;
}

export const NfcScanButton = ({
  orderId,
  onDeviceFound,
  onNotInOrder,
  onNotFound,
  disabled = false
}: NfcScanButtonProps) => {
  const { isSupported, isScanning, error, lastRead, startScan, stopScan, clearError, clearLastRead } = useNfcScanner();
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const handleScanClick = useCallback(() => {
    if (isScanning) {
      stopScan();
    } else {
      setLookupError(null);
      clearError();
      clearLastRead();
      void startScan();
    }
  }, [isScanning, stopScan, startScan, clearError, clearLastRead]);

  useEffect(() => {
    if (!lastRead || !lastRead.serialNumber) {
      return;
    }

    const lookupDevice = async () => {
      setIsLookingUp(true);
      setLookupError(null);

      try {
        const tagId = lastRead.serialNumber;
        const response = await api.get<NfcTagLookupResponse>(
          `technicians/me/inspection-orders/${orderId}/devices/by-nfc-tag/${encodeURIComponent(tagId)}`
        );

        const result = response as unknown as NfcTagLookupResponse;

        if (!result.found) {
          onNotFound();
        } else if (result.inCurrentOrder && result.orderDevice) {
          onDeviceFound(result);
        } else {
          onNotInOrder(result);
        }
      } catch (lookupErr) {
        console.error('NFC lookup error:', lookupErr);
        setLookupError('Gerät konnte nicht gefunden werden');
      } finally {
        setIsLookingUp(false);
        clearLastRead();
      }
    };

    void lookupDevice();
  }, [lastRead, orderId, onDeviceFound, onNotInOrder, onNotFound, clearLastRead]);

  if (!isSupported) {
    return null;
  }

  const displayError = lookupError || error;

  return (
    <div className="nfc-scan-button-container">
      <button
        type="button"
        className={`nfc-scan-button ${isScanning ? 'nfc-scan-button--scanning' : ''}`}
        onClick={handleScanClick}
        disabled={disabled || isLookingUp}
        aria-label={isScanning ? 'NFC-Scan abbrechen' : 'NFC-Tag scannen'}
      >
        <span className="nfc-scan-button__icon" aria-hidden="true">
          {isScanning ? '📡' : '📱'}
        </span>
        <span className="nfc-scan-button__label">
          {isLookingUp ? 'Suche...' : isScanning ? 'Scannen...' : 'NFC scannen'}
        </span>
      </button>
      {displayError && (
        <div className="nfc-scan-button__error" role="alert">
          {displayError}
        </div>
      )}
    </div>
  );
};
